import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';

const STEPS = [
  {
    emoji: '🔍',
    step: '01',
    title: 'Browse Without the BS',
    description: 'Real photos, real pricing, free CarFax on every car. No "call for price." No mystery packages. Just actual numbers.',
    cta: { label: 'Browse Inventory', to: '/inventory' },
  },
  {
    emoji: '💬',
    step: '02',
    title: 'Talk to a Real Human',
    description: 'Ask anything. Straight answers — no scripts, no pressure, no "closer" from the back office. We consult. You decide.',
    cta: { label: 'Contact Us', to: '/contact' },
  },
  {
    emoji: '💳',
    step: '03',
    title: 'Get Approved Fast',
    description: 'Same-day financing available. Bad credit welcome. We work with lenders who deal in reality, not just ideal scenarios.',
    cta: { label: 'Apply Now', to: '/apply-for-financing' },
  },
  {
    emoji: '🚗',
    step: '04',
    title: 'Drive Home Happy',
    description: 'We handle the paperwork. You handle the good feeling. Know exactly what you paid — and that a dog in Oregon is rooting for you.',
    cta: { label: 'See How It Works', to: '/how-it-works' },
  },
];

const AUTO_ADVANCE_MS = 3500;

export default function BuyingProcess() {
  const [activeStep, setActiveStep] = useState(0);

  const prefersReducedMotion = useRef(
    typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  const userInteractedRef = useRef(false);
  const pauseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Touch / drag tracking for the carousel track
  const touchStartXRef = useRef<number | null>(null);
  const touchStartYRef = useRef<number | null>(null);
  const isDraggingRef = useRef(false);

  const goToStep = useCallback((index: number, byUser = true) => {
    setActiveStep(index);
    if (byUser) {
      userInteractedRef.current = true;
      if (pauseTimerRef.current) clearTimeout(pauseTimerRef.current);
      pauseTimerRef.current = setTimeout(() => {
        userInteractedRef.current = false;
      }, 8000);
    }
  }, []);

  useEffect(() => {
    if (prefersReducedMotion.current) return;
    const t = setInterval(() => {
      if (!userInteractedRef.current) {
        setActiveStep(p => (p + 1) % STEPS.length);
      }
    }, AUTO_ADVANCE_MS);
    return () => clearInterval(t);
  }, []);

  // ── Keyboard navigation (desktop grid) ──────────────────────────────────
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      const next = (activeStep + 1) % STEPS.length;
      goToStep(next);
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      goToStep((activeStep - 1 + STEPS.length) % STEPS.length);
    } else if (e.key === 'Home') {
      e.preventDefault(); goToStep(0);
    } else if (e.key === 'End') {
      e.preventDefault(); goToStep(STEPS.length - 1);
    }
  }, [activeStep, goToStep]);

  // ── Touch handlers for the mobile carousel track ─────────────────────────
  const handleTrackTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartXRef.current = e.touches[0].clientX;
    touchStartYRef.current = e.touches[0].clientY;
    isDraggingRef.current = false;
  }, []);

  const handleTrackTouchMove = useCallback((e: React.TouchEvent) => {
    if (touchStartXRef.current === null || touchStartYRef.current === null) return;
    const dx = Math.abs(e.touches[0].clientX - touchStartXRef.current);
    const dy = Math.abs(e.touches[0].clientY - touchStartYRef.current);
    // Only lock horizontal scroll if clearly swiping sideways
    if (dx > dy && dx > 8) {
      isDraggingRef.current = true;
      e.preventDefault();
    }
  }, []);

  const handleTrackTouchEnd = useCallback((e: React.TouchEvent) => {
    if (touchStartXRef.current === null) return;
    const deltaX = e.changedTouches[0].clientX - touchStartXRef.current;
    const THRESHOLD = 45;
    if (isDraggingRef.current) {
      if (deltaX < -THRESHOLD) goToStep((activeStep + 1) % STEPS.length);
      else if (deltaX > THRESHOLD) goToStep((activeStep - 1 + STEPS.length) % STEPS.length);
    }
    touchStartXRef.current = null;
    touchStartYRef.current = null;
    isDraggingRef.current = false;
  }, [activeStep, goToStep]);

  // Prevent layout thrashing — removed getBoundingClientRect read that caused 93ms forced reflow
  const handleStepClick = useCallback((index: number) => {
    setActiveStep(index);
  }, []);

  const transition = prefersReducedMotion.current ? 'none' : 'transform 0.38s cubic-bezier(0.4,0,0.2,1)';

  return (
    <section className="py-10 md:py-16 bg-[#F7F5F0]" aria-label="How buying works">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3 mb-8">
          <div>
            <span className="inline-block px-3 py-1 bg-[#E8B84B]/10 border border-[#E8B84B]/30 text-[#5C2800] text-xs font-bold uppercase tracking-widest rounded-full mb-2">
              Simple Process
            </span>
            <h2 className="text-2xl md:text-4xl font-bold text-[#1A2332] leading-tight" style={{ fontFamily: 'Oswald, sans-serif' }}>
              Four Steps.<br />Zero Games.
            </h2>
          </div>
          <p className="text-sm text-[#374151] max-w-sm lg:text-right leading-relaxed hidden sm:block">
            Most customers are in and out in under 2 hours. No marathon negotiations. No back-office theater.
          </p>
        </div>

        {/* ── MOBILE CAROUSEL (hidden on sm+) ─────────────────────────────── */}
        <div className="sm:hidden">
          {/* Overflow clip wrapper */}
          <div
            className="overflow-hidden rounded-2xl"
            onTouchStart={handleTrackTouchStart}
            onTouchMove={handleTrackTouchMove}
            onTouchEnd={handleTrackTouchEnd}
          >
            {/* Sliding track */}
            <div
              className="flex"
              style={{
                transform: `translateX(-${activeStep * 100}%)`,
                transition,
                willChange: 'transform',
              }}
            >
              {STEPS.map((step, i) => {
                const isActive = activeStep === i;
                return (
                  <div
                    key={i}
                    className="w-full flex-shrink-0 p-6 flex flex-col"
                    style={{
                      minHeight: '260px',
                      backgroundColor: '#1A2332',
                      borderRadius: '1rem',
                    }}
                    aria-hidden={!isActive}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div
                        className="w-11 h-11 flex items-center justify-center rounded-xl text-2xl"
                        style={{ backgroundColor: '#E8B84B' }}
                        aria-hidden="true"
                      >
                        {step.emoji}
                      </div>
                      <span
                        className="text-4xl font-black"
                        style={{ fontFamily: 'Oswald, sans-serif', color: 'rgba(232,184,75,0.7)' }}
                        aria-hidden="true"
                      >
                        {step.step}
                      </span>
                    </div>

                    <h3
                      className="text-lg font-bold mb-2 text-white"
                      style={{ fontFamily: 'Oswald, sans-serif' }}
                    >
                      {step.title}
                    </h3>
                    <p className="text-sm leading-relaxed flex-grow" style={{ color: 'rgba(255,255,255,0.78)' }}>
                      {step.description}
                    </p>

                    <Link
                      to={step.cta.to}
                      className="inline-flex items-center gap-1 mt-5 text-sm font-bold whitespace-nowrap px-4 py-2.5 rounded-lg self-start"
                      style={{ backgroundColor: '#E8B84B', color: '#1A2332' }}
                    >
                      {step.cta.label} <span aria-hidden="true">→</span>
                    </Link>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Mobile controls row */}
          <div className="flex items-center justify-between mt-5 px-1">
            {/* Prev */}
            <button
              onClick={() => goToStep((activeStep - 1 + STEPS.length) % STEPS.length)}
              aria-label="Previous step"
              className="w-10 h-10 flex items-center justify-center rounded-full border-2 border-[#E8B84B]/40 bg-white text-[#1A2332] cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E8B84B]"
              style={{ opacity: activeStep === 0 ? 0.35 : 1 }}
            >
              <i className="ri-arrow-left-s-line text-xl" />
            </button>

            {/* Dot indicators */}
            <ol className="flex items-center gap-2" aria-label="Step indicators">
              {STEPS.map((step, index) => {
                const isActive = activeStep === index;
                return (
                  <li key={index}>
                    <button
                      onClick={() => goToStep(index)}
                      aria-label={`Go to step ${index + 1}: ${step.title}`}
                      aria-current={isActive ? 'step' : undefined}
                      className="flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E8B84B] rounded-full"
                      style={{ width: '36px', height: '36px', background: 'transparent' }}
                    >
                      <span
                        className="block rounded-full pointer-events-none"
                        style={{
                          width: isActive ? '28px' : '10px',
                          height: '10px',
                          backgroundColor: isActive ? '#E8B84B' : '#CBD5E0',
                          transition: prefersReducedMotion.current ? 'none' : 'width 0.3s ease, background-color 0.3s ease',
                        }}
                      />
                    </button>
                  </li>
                );
              })}
            </ol>

            {/* Next */}
            <button
              onClick={() => goToStep((activeStep + 1) % STEPS.length)}
              aria-label="Next step"
              className="w-10 h-10 flex items-center justify-center rounded-full border-2 border-[#E8B84B]/40 bg-white text-[#1A2332] cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E8B84B]"
              style={{ opacity: activeStep === STEPS.length - 1 ? 0.35 : 1 }}
            >
              <i className="ri-arrow-right-s-line text-xl" />
            </button>
          </div>

          {/* Swipe hint */}
          <p className="text-center text-xs text-[#374151] mt-3" aria-hidden="true">
            Swipe or tap arrows to browse steps
          </p>
        </div>

        {/* ── DESKTOP GRID (hidden on mobile) ─────────────────────────────── */}
        <div
          role="group"
          aria-label="Buying process steps — use arrow keys to navigate"
          className="hidden sm:grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10 outline-none"
          onKeyDown={handleKeyDown}
        >
          {STEPS.map((step, i) => {
            const isActive = activeStep === i;
            return (
              <button
                key={i}
                data-step-card={String(i)}
                onClick={() => handleStepClick(i)}
                aria-pressed={isActive}
                aria-label={`Step ${step.step}: ${step.title}`}
                tabIndex={isActive ? 0 : -1}
                className="text-left p-6 rounded-2xl border-2 cursor-pointer group focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E8B84B] will-change-transform min-h-[280px] flex flex-col"
                style={{
                  backgroundColor: isActive ? '#1A2332' : '#FFFFFF',
                  borderColor: isActive ? '#E8B84B' : '#E8E2D6',
                  boxShadow: isActive
                    ? '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)'
                    : '0 1px 2px 0 rgba(0,0,0,0.05)',
                  transition: prefersReducedMotion.current ? 'none' : 'background-color 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease',
                }}
              >
                <div className="flex items-center justify-between mb-4">
                  <div
                    className="w-10 h-10 flex items-center justify-center rounded-xl text-xl"
                    style={{ backgroundColor: isActive ? '#E8B84B' : 'rgba(232,184,75,0.1)' }}
                    aria-hidden="true"
                  >
                    {step.emoji}
                  </div>
                  <span
                    className="text-3xl font-black"
                    style={{ fontFamily: 'Oswald, sans-serif', color: isActive ? 'rgba(232,184,75,0.85)' : '#4B5563' }}
                    aria-hidden="true"
                  >
                    {step.step}
                  </span>
                </div>

                <h3
                  className="text-base font-bold mb-2"
                  style={{ fontFamily: 'Oswald, sans-serif', color: isActive ? '#FFFFFF' : '#1A2332' }}
                >
                  {step.title}
                </h3>
                <p
                  className="text-xs leading-relaxed flex-grow"
                  style={{ color: isActive ? 'rgba(255,255,255,0.8)' : '#374151' }}
                >
                  {step.description}
                </p>

                {isActive && (
                  <Link
                    to={step.cta.to}
                    onClick={e => e.stopPropagation()}
                    className="inline-flex items-center justify-center gap-1 mt-4 text-sm font-bold whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:rounded min-h-[44px] px-3 py-2 rounded-md"
                    style={{ color: '#E8B84B' }}
                    onMouseEnter={e => (e.currentTarget.style.color = '#FFFFFF')}
                    onMouseLeave={e => (e.currentTarget.style.color = '#E8B84B')}
                  >
                    {step.cta.label} <span aria-hidden="true">→</span>
                  </Link>
                )}
              </button>
            );
          })}
        </div>

        {/* Desktop dot indicators */}
        <div aria-live="polite" aria-atomic="true" className="sr-only">
          Step {activeStep + 1} of {STEPS.length}: {STEPS[activeStep].title}
        </div>

        <ol className="hidden sm:flex justify-center gap-2 mb-12" aria-label="Buying process step indicators">
          {STEPS.map((step, index) => {
            const isActive = activeStep === index;
            return (
              <li key={index}>
                <button
                  onClick={() => goToStep(index)}
                  className="flex items-center justify-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E8B84B] focus-visible:ring-offset-2"
                  style={{ width: '44px', height: '44px', background: 'transparent' }}
                  aria-label={`Go to step ${index + 1} of ${STEPS.length}: ${step.title}`}
                  aria-current={isActive ? 'step' : undefined}
                >
                  <span
                    className="block rounded-full pointer-events-none"
                    style={{
                      width: '32px',
                      height: '12px',
                      transformOrigin: 'center',
                      backgroundColor: isActive ? '#E8B84B' : '#D1D5DB',
                      transform: isActive ? 'scaleX(1)' : 'scaleX(0.375)',
                      transition: prefersReducedMotion.current
                        ? 'none'
                        : 'transform 0.3s ease-out, background-color 0.3s ease-out',
                    }}
                  />
                </button>
              </li>
            );
          })}
        </ol>

        <div className="text-center mt-6">
          <Link
            to="/how-it-works"
            className="inline-flex items-center gap-2 px-8 py-3 bg-teal-700 text-white font-bold rounded-lg hover:bg-teal-800 transition-colors whitespace-nowrap cursor-pointer min-h-[44px]"
          >
            See the Full Process
            <span aria-hidden="true">→</span>
          </Link>
        </div>

      </div>
    </section>
  );
}
